import { useNavigate } from "react-router-dom";
import React from "react";
import { motion } from "framer-motion";
import { HERO_IMG, EVENT, LOGOS, isRegistrationClosed } from "@/lib/constants";
import { ArrowRight, MapPin, Calendar, Clock, Lock, Unlock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LogoPair } from "@/components/LogoPair";
import LanguageToggle from "@/components/LanguageToggle";
import EventDateBadge from "@/components/EventDateBadge";
import VisitorPrizeNotice from "@/components/VisitorPrizeNotice";
import HeroWordmark from "@/components/HeroWordmark";
import { SponsorLineup, AuthorityRow } from "@/components/SponsorLineup";

export default function Landing() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const ArrowIcon = isRTL ? null : ArrowRight;
  const closed = isRegistrationClosed();

  return (
    <div dir="ltr" className="relative min-h-screen overflow-hidden bg-black">
      {/* Hero background */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "saturate(1.1)",
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
      <div className="absolute inset-0 z-0 bg-black/30" />

      {/* Top bar — language + admin only, tight to the top */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 flex items-center justify-end px-6 sm:px-12 pt-4"
      >
        <div className="flex items-center gap-6">
          <LanguageToggle />
          <button
            onClick={() => navigate("/admin")}
            data-testid="admin-link"
            className="text-xs tracking-[0.2em] uppercase text-white/40 hover:text-[#F5C518] transition-colors font-body"
          >
            {t("admin")}
          </button>
        </div>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center px-6 sm:px-12 min-h-[calc(100vh-60px)] pt-2 pb-16">
        {/* Logos */}
        {/* Hero group (title + subtitle) */}
        <div className="w-full flex flex-col items-center">
        {/* Headline — canonical two-line lockup. Alignment logic lives
            inside HeroWordmark so it stays in sync with the intro
            header and is measured against the actual Outfit metrics
            at every breakpoint (T's stem-centre = h's left edge). */}
        <HeroWordmark testId="hero-title" />

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          style={{ transform: "translateY(8px)" }}
          className="font-body text-white text-[19px] sm:text-[21px] max-w-2xl mx-auto text-center mb-4"
        >
          {t("heroSub")}
        </motion.p>

        <AuthorityRow />

        {/* Shared event-date badge — one instance per screen.
            Landing offset (Feb 2026 owner directive): slight downward
            nudge from the hero subhead so the enlarged badge breathes. */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="flex justify-center mt-8 mb-8"
        >
          <EventDateBadge />
        </motion.div>

        <SponsorLineup />

        {/* CTA + registration status. Sep 13 2026: wrapped in
            `flex flex-col items-center` so the Begin Application
            button centers within the outer Hero wrapper on every
            viewport (LTR + RTL). The duplicate small clock-icon
            "REGISTRATION IS OPEN" badge was removed — the
            authoritative status notice remains further down under
            `registration-open-cta`. Open/closed state and CTA
            navigation are unchanged. */}
        {/* Sep 21 2026 (Rev 7) — dual-path entry replaces the single
            Begin-Application CTA. Vehicle path (existing application
            funnel) is presented first, visitor path (new free
            registration) second, per owner directive. Existing
            regClosedTitle/regClosedBody are preserved as the
            authoritative status line beneath the cards. */}
        {closed ? null : (
          <div className="flex flex-col items-center w-full mt-6" data-testid="landing-choice">
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              dir={isRTL ? "rtl" : "ltr"}
              className={`font-heading font-extrabold text-white mb-6 tracking-wide ${isRTL ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`}
              style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
              data-testid="landing-choice-title"
            >
              {t("chooseHowTitle")}
            </motion.h2>
            <div className={`grid gap-4 sm:gap-6 w-full max-w-3xl items-start ${isRTL ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
              {/* Column 1 — Vehicle application (Prize notice NOT wired here) */}
              <div>
              {/* Card 1 — Register Your Vehicle (existing application flow) */}
              <motion.button
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.95 }}
                type="button"
                onClick={() => navigate("/apply")}
                data-testid="landing-vehicle-card"
                dir={isRTL ? "rtl" : "ltr"}
                className="group text-center w-full border border-[#F5C518]/60 bg-black/50 backdrop-blur-sm hover:border-[#F5C518] hover:bg-[#F5C518]/[0.06] transition-colors p-6 sm:p-7 min-h-[184px] flex flex-col justify-between items-center"
                style={{ textAlign: "center" }}
              >
                <div className="w-full">
                  <div
                    className={`font-heading font-extrabold text-white text-center ${isRTL ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`}
                    style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
                    data-testid="landing-vehicle-title"
                  >
                    {t("vehicleCardTitle")}
                  </div>
                  <p
                    className={`font-body text-white/70 mt-2 text-center ${isRTL ? "text-base sm:text-lg leading-relaxed" : "text-sm sm:text-base leading-relaxed"}`}
                    style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
                    data-testid="landing-vehicle-helper"
                  >
                    {t("vehicleCardHelper")}
                  </p>
                </div>
                <div className={`mt-4 inline-flex items-center gap-2 text-[#F5C518] font-heading tracking-[0.2em] uppercase text-sm sm:text-base justify-center ${isRTL ? "flex-row-reverse" : ""}`}>
                  <span data-testid="landing-vehicle-cta">{t("vehicleCardCta")}</span>
                  {isRTL
                    ? <ArrowRight className="w-4 h-4 rotate-180 transition-transform group-hover:-translate-x-1" />
                    : <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
                </div>
              </motion.button>
              </div>

              {/* Column 2 — Visitor registration + scoped Prize Draw
                  notice, so on desktop the notice sits directly under
                  the visitor card, and on mobile it renders
                  immediately after the visitor card. It never applies
                  to the vehicle column. */}
              <div className="flex flex-col gap-4">
              <motion.button
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.05 }}
                type="button"
                onClick={() => navigate("/visitor")}
                data-testid="landing-visitor-card"
                dir={isRTL ? "rtl" : "ltr"}
                className="group text-center w-full border border-white/25 bg-black/50 backdrop-blur-sm hover:border-white hover:bg-white/[0.04] transition-colors p-6 sm:p-7 min-h-[184px] flex flex-col justify-between items-center"
                style={{ textAlign: "center" }}
              >
                <div className="w-full">
                  <div
                    className={`font-heading font-extrabold text-white text-center ${isRTL ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`}
                    style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
                    data-testid="landing-visitor-title"
                  >
                    {t("visitorCardTitle")}
                  </div>
                  <p
                    className={`font-body text-white/70 mt-2 text-center ${isRTL ? "text-base sm:text-lg leading-relaxed" : "text-sm sm:text-base leading-relaxed"}`}
                    style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
                    data-testid="landing-visitor-helper"
                  >
                    {t("visitorCardHelper")}
                  </p>
                </div>
                <div className={`mt-4 inline-flex items-center gap-2 text-white/90 font-heading tracking-[0.2em] uppercase text-sm sm:text-base justify-center ${isRTL ? "flex-row-reverse" : ""}`}>
                  <span data-testid="landing-visitor-cta">{t("visitorCardCta")}</span>
                  {isRTL
                    ? <ArrowRight className="w-4 h-4 rotate-180 transition-transform group-hover:-translate-x-1" />
                    : <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
                </div>
              </motion.button>
              {/* Feb 2026 owner directive: notice is strictly scoped
                  to the visitor choice — it applies to completed
                  VISITOR registrations only and must not imply the
                  vehicle-application flow is entered. */}
              <VisitorPrizeNotice
                className=""
              />
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Lower block */}
        <div className="flex flex-col items-center">
        {/* Event pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="flex flex-col items-center gap-3 text-center max-w-2xl mt-14 mb-2"
          data-testid="registration-status"
        >
          <div className={`flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 max-w-full`} data-testid="event-detail" style={{ transform: "translateY(-15px)" }}>
            <div className={`flex items-center gap-2 px-5 py-2.5 border border-white/10 bg-black/40 backdrop-blur-sm justify-center max-w-full ${isRTL ? "flex-row-reverse w-fit" : "sm:min-w-[340px]"}`}>
              <Calendar className="w-4 h-4 text-[#F5C518] shrink-0" />
              <span
                className={`font-body text-white/80 ${isRTL ? "text-center text-lg sm:text-xl sm:whitespace-nowrap tracking-normal" : "text-sm sm:text-base tracking-wide"}`}
                style={isRTL ? { fontVariantNumeric: "tabular-nums", fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
              >
                {t("evtDate")}
              </span>
            </div>
            <div className={`flex items-center gap-2 px-5 py-2.5 border border-white/10 bg-black/40 backdrop-blur-sm justify-center max-w-full ${isRTL ? "flex-row-reverse w-fit" : "sm:min-w-[170px]"}`}>
              <Clock className="w-4 h-4 text-[#F5C518] shrink-0" />
              <span
                className={`font-body text-white/80 ${isRTL ? "text-base sm:text-lg sm:whitespace-nowrap tracking-normal" : "text-sm sm:text-base tracking-wide"}`}
                style={isRTL ? { fontVariantNumeric: "tabular-nums", fontWeight: 500, fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
              >
                {t("evtTime")}
              </span>
            </div>
          </div>
          <div
            data-testid="registration-open-cta"
            aria-label={t("regClosedTitle")}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 border border-[#F5C518]/40 bg-[#F5C518]/10 mt-3 max-w-full ${isRTL ? "flex-row-reverse" : ""}`}
          >
            <Unlock className="w-3.5 h-3.5 text-[#F5C518]" />
            <span
              className={`font-heading font-bold text-[#F5C518] uppercase ${isRTL ? "text-xs sm:text-sm tracking-normal" : "text-xs sm:text-sm tracking-[0.15em]"}`}
              style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
            >
              {t("regClosedTitle")}
            </span>
          </div>
          <p
            dir={isRTL ? "rtl" : "ltr"}
            className={`font-body text-white/85 leading-relaxed max-w-2xl mx-auto ${isRTL ? "text-lg sm:text-xl" : "text-base sm:text-lg"}`}
            style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}
          >
            {t("regClosedBody").split("§GATHERING§").map((chunk, i, arr) => (
              <React.Fragment key={i}>
                {chunk}
                {i < arr.length - 1 && (
                  <span
                    dir="ltr"
                    style={{
                      unicodeBidi: "isolate-override",
                      direction: "ltr",
                      display: "inline-block",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"\u202D"}THE <span className="text-[#F5C518]">GATHERING</span>{"\u202C"}
                  </span>
                )}
              </React.Fragment>
            ))}
          </p>
          <p
            className={`font-body text-white mt-2 ${isRTL ? "text-base sm:text-lg md:text-xl font-semibold leading-relaxed max-w-3xl mx-auto tracking-normal px-4" : "text-sm tracking-[0.15em] uppercase"}`}
            style={isRTL ? { fontFamily: "'Noto Sans Arabic', sans-serif", letterSpacing: "normal" } : undefined}
            data-testid="landing-footer-line"
          >
            {t("footerLine")}
          </p>
        </motion.div>
        </div>
      </div>
    </div>
  );
}
